import React, {useEffect, useState} from "react";
import {Button, Grid, TextField, Typography, useMediaQuery,} from "@mui/material";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import Checkbox from "../../../components/shared/checkbox/Checkbox";

export default function CheckboxInput({
                                          data,
                                          addQuestion,
                                          deleteQuestionsAfterIndex,
                                          goToNextQuestion,
                                          isVisible,
                                          addToUpdateQuestions,
                                          deleteFromQuestionToUpdate,
                                      }) {
    const {t} = useTranslation();
    const isMobile = useMediaQuery("(max-width:900px)");
    const [checked, setChecked] = useState({}); // Tracks the state for all selections
    const language = localStorage.getItem("i18nextLng");

    // Initialize `checked` state with `currentValue` or set defaults
    useEffect(() => {
        const initialChecked = {};
        data.choices.forEach((choice) => {
            if (data?.currentValue && data?.currentValue[choice.key] !== undefined && data?.currentValue[choice.key] !== 0) {
                initialChecked[choice.key] = data?.currentValue[choice.key];
            } else {
                initialChecked[choice.key] = null; // Unselected state
            }
        });
        setChecked(initialChecked);
    }, [data?.choices, data?.currentValue]);

    // Handle checkbox selection logic
    const handleSelection = (choiceKey, hasNextQuestion) => {
        deleteQuestionsAfterIndex(data);
        deleteFromQuestionToUpdate(data);
        const updatedChecked = {...checked};
        if (hasNextQuestion) {
            data?.choices.forEach((choice) => {
                if (!choice.question_object) updatedChecked[choice.key] = null;
            });
            updatedChecked[choiceKey] =
                updatedChecked[choiceKey] === null ? "" : null;
        } else {
            data?.choices.forEach((choice) => {
                updatedChecked[choice.key] = null;
            });
            updatedChecked[choiceKey] = true;
        }
        setChecked(updatedChecked);
    };

    // Handle input field updates for options with `question_object`
    const handleInputChange = (choiceKey, value) => {
        const updatedChecked = {...checked};
        // Convert the input value to a number
        const numericValue = Number(value);
        if (!isNaN(numericValue)) {
            if (numericValue > 0) {
                // If the value is a valid number greater than 0, update the state
                updatedChecked[choiceKey] = numericValue;
            } else if (numericValue === 0) {
                // If the value is 0, reset the state to null and do not update the input
                updatedChecked[choiceKey] = null;
            }
        } else {
            // If the input is not a valid number, reset the state to null
            updatedChecked[choiceKey] = null;
        }
        setChecked(updatedChecked);
    };

    function areObjectsEqual(obj1, obj2) {
        // If both are null or undefined, they are equal
        if (obj1 === obj2) return true;

        // If one is null/undefined and the other isn't, they are not equal
        if (!obj1 || !obj2) return false;

        // If types are different, they are not equal
        if (typeof obj1 !== typeof obj2) return false;

        // Handle arrays
        if (Array.isArray(obj1) && Array.isArray(obj2)) {
            if (obj1.length !== obj2.length) return false;
            for (let i = 0; i < obj1.length; i++) {
                if (!areObjectsEqual(obj1[i], obj2[i])) return false;
            }
            return true;
        }

        // Handle objects
        if (typeof obj1 === 'object' && typeof obj2 === 'object') {
            const keys1 = Object.keys(obj1);
            const keys2 = Object.keys(obj2);

            // If the number of keys is different, they are not equal
            if (keys1.length !== keys2.length) return false;

            // Check each key-value pair
            for (let key of keys1) {
                if (!keys2.includes(key) || !areObjectsEqual(obj1[key], obj2[key])) {
                    return false;
                }
            }

            return true;
        }

        // For primitive types, use strict equality
        return obj1 === obj2;
    }

    // Handle the "Next" button click
    const handleNextClick = () => {

        if (data && checked) {
            const processedChecked1 = Object.keys(checked).reduce((acc, key) => {
                if (checked[key] !== null) { // Only add keys that are not null
                    if (checked[key] === true) {
                        acc[key] = 1; // Replace true with 1
                    } else {
                        acc[key] = checked[key]; // Keep other values unchanged
                    }
                }
                return acc;
            }, {});
            if (!areObjectsEqual(processedChecked1, data.currentValue)) {
                const processedChecked = Object.keys(checked).reduce((acc, key) => {
                    if (checked[key] === null) {
                        acc[key] = 0; // Replace null with 0
                    } else if (checked[key] === true) {
                        acc[key] = 1; // Replace true with 1
                    } else {
                        acc[key] = checked[key]; // Keep other values unchanged
                    }
                    return acc;
                }, {});

                // Call the function with the processed data
                addToUpdateQuestions(data, processedChecked);
            }

        }

        if (data?.required && Object.values(checked).every((value) => value === null)) {
            return; // Prevent moving to the next question if required fields are empty
        }

        const selectedChoices = data?.choices.filter(
            (choice) => checked[choice.key] !== null && choice.question_object
        );
        selectedChoices.forEach((choice) => {
            addQuestion(choice.question_object);
        });

        if (selectedChoices.length === 0) {
            goToNextQuestion();
        }
    };

    function handleSkipClick() {
        if (data?.associated_step_id === "4c2ea3bd-831a-4a53-8cc7-5ec1b26d628b") {
            if (data?.question_object) {

                addQuestion(data?.question_object);
            } else {
                // Otherwise, move to the next question
                goToNextQuestion(null, true);
            }
        } else {
            goToNextQuestion(null, true);
        }
    }

    return (
        <Box style={{padding: "20px"}}>
            {/* Display question */}
            <Typography variant="subtitle1" sx={{mb: 2}}>
                {language === "fr" ? data?.question_fr : data.question}
            </Typography>
            <Grid container direction="column" spacing={2}>
                {data?.choices.map((choice) => (
                    <Grid
                        key={choice.id}
                        item
                        container
                        alignItems="flex-start" // Align items at the top (useful for mobile)
                        spacing={2}

                    >
                        {/* Checkbox */}
                        <Grid
                            item
                            xs={12} // Full width on mobile
                            sm={12} // Full width on small screens
                            md={6}  // Half width on medium screens and larger
                        >
                            <Checkbox
                                title={t(choice.key)}
                                checked={checked[choice.key] !== null}
                                value={choice.key}
                                nextQuestionHandler={() =>
                                    handleSelection(choice.key, !!choice.question_object)
                                }
                            />
                        </Grid>

                        {/* Render input for options with `question_object` */}
                        {checked[choice.key] !== null && choice.question_object && (
                            <Grid
                                item
                                xs={12}
                                sm={12}
                                md={6}

                            >
                                <TextField

                                    placeholder={
                                        language === "fr"
                                            ? "spécifier le nombre d'incidents"
                                            : "Specify incident Number"
                                    }
                                    value={checked[choice.key] || ""}
                                    onChange={(e) =>
                                        handleInputChange(choice.key, e.target.value)
                                    }
                                    type="number"
                                    variant="outlined"
                                    fullWidth
                                />
                            </Grid>
                        )}
                    </Grid>
                ))}
            </Grid>


            <div style={{display: "flex", gap: "10px", alignItems: "center"}}>
                {isVisible(data) && (
                    <Button
                        variant="contained"
                        color="secondary"
                        style={{
                            height: "50px",
                            width: data?.associated_step_id !== "4c2ea3bd-831a-4a53-8cc7-5ec1b26d628b" ? "50%" : "50%", // Each button takes 45% of the container width
                            marginTop: "20px",
                        }}
                        onClick={handleNextClick}
                        disabled={
                            Object.values(checked).every((value) => value === null) || // No selections
                            data.choices.some((
                                    choice // Check for incomplete input fields
                                ) =>
                                    checked[choice.key] !== null && // Checkbox is selected
                                    choice.question_object && // Has an associated input field
                                    (!checked[choice.key] || checked[choice.key] === "") // Input field is empty
                            )} // Disable if required fields are empty
                    >
                        {data?.last_step_question_2 ? t("finish") : t("next")}
                    </Button>
                )}
                {isVisible(data) && (
                    <Button
                        style={{
                            height: "50px",
                            width: "50%", // Each button takes 45% of the container width
                            marginTop: "20px",
                        }}
                        color="secondary"
                        variant="contained"
                        onClick={handleSkipClick}
                    >
                        {t("skip")}
                    </Button>
                )}
            </div>
        </Box>
    );

}